import { Component } from '@theme/component';

const CARD_SELECTOR = '.landing-floating-media';
const IN_VIEW_CLASS = 'landing-floating-media--in-view';

/**
 * How often the cards' positions are sampled. The loop takes 45s to travel one tile, so a card
 * moves on the order of a pixel between samples at this rate -- far finer than the eye can
 * catch a state change, and a fraction of the work of testing every frame.
 */
const SAMPLE_INTERVAL_MS = 100;

/**
 * Subpixel layout and fractional device pixel ratios make an exactly-flush edge land a hair
 * either side of the boundary, so "inside" gets a pixel of slack. Nothing meaningfully cropped
 * can pass through a gap this small.
 */
const EDGE_TOLERANCE_PX = 1;

/**
 * Closes the seam in the landing hero's floating grid loop, and lights each card up while it is
 * entirely on screen.
 *
 * The track travels exactly one tile height (see `blocks/_landing-floating-grid.liquid`). This
 * appends a second copy of the tile one tile below, so at the instant the track resets the copy
 * is sitting precisely where the original started and the loop reads as continuous.
 *
 * One copy is always enough, and the geometry is fixed, so nothing here measures the layout to
 * build the loop -- which is what keeps the effect free of reflow and layout shift. That is also
 * why this does not reuse `assets/marquee.js`: that component measures its content and clones it
 * N times to fill a width, work this loop does not need.
 *
 * The motion itself lives in CSS, so it honours `prefers-reduced-motion` and pauses on hover even
 * if this module never loads. Without it the cards just render where the design puts them, at
 * their resting opacity.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tile - The authored card layer.
 *
 * @extends Component<Refs>
 */
class LandingFloatingGrid extends Component {
  requiredRefs = ['tile'];

  /** @type {HTMLElement | null} */
  #copy = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {number} */
  #lastSample = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#addCopy();
    this.#watchVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopSampling();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#copy?.remove();
    this.#copy = null;
  }

  #addCopy() {
    if (this.#copy) return;

    const { tile } = this.refs;
    const copy = /** @type {HTMLElement} */ (tile.cloneNode(true));

    // A second `ref="tile"` would make the component's own lookup ambiguous, and duplicated
    // `id`s are invalid however well hidden the copy is. The host element carries
    // `aria-hidden`, so the copy is already out of the accessibility tree with the original.
    copy.removeAttribute('ref');

    for (const element of copy.querySelectorAll('[ref], [id]')) {
      element.removeAttribute('ref');
      element.removeAttribute('id');
    }

    this.#copy = copy;
    tile.after(copy);
  }

  /**
   * Marks each card while it sits entirely inside the visible part of the layer, which the
   * stylesheet uses to bring it up from its resting opacity.
   *
   * **This deliberately does NOT observe the cards with an IntersectionObserver, and that is not
   * an oversight.** A composited `transform` animation moves the cards on the compositor without
   * invalidating layout, so the main thread never recomputes intersections and the observer goes
   * silent: measured on this page (2026-09-02), a card travelled from `bottom: 167` to
   * `bottom: -73` -- completely off the top of the viewport -- while an observer bound to it
   * fired **zero** callbacks in 17 seconds and the card stayed lit the whole way. Sampling the
   * positions is the only thing that actually tracks this animation.
   *
   * The sampling is cheap precisely because of the same property: a transform does not dirty
   * layout, so these reads hit a clean layout tree rather than forcing reflow. All the rects are
   * read before any class is written, so the loop cannot thrash even once a write lands.
   *
   * An IntersectionObserver IS used, on the host element -- which never moves -- purely to stop
   * the sampling while the hero is scrolled away.
   */
  #watchVisibility() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) this.#startSampling();
        else this.#stopSampling();
      },
      { threshold: 0 }
    );

    this.#observer.observe(this);
  }

  #startSampling() {
    if (this.#frame !== null) return;

    const tick = (/** @type {number} */ now) => {
      this.#frame = requestAnimationFrame(tick);

      if (now - this.#lastSample < SAMPLE_INTERVAL_MS) return;

      this.#lastSample = now;
      this.#syncCards();
    };

    this.#frame = requestAnimationFrame(tick);
  }

  #stopSampling() {
    if (this.#frame === null) return;

    cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #syncCards() {
    const host = this.getBoundingClientRect();

    // The layer is clipped by the section as well as by the viewport, so "fully visible" is
    // measured against whichever edge is tighter.
    const top = Math.max(host.top, 0) - EDGE_TOLERANCE_PX;
    const left = Math.max(host.left, 0) - EDGE_TOLERANCE_PX;
    const right = Math.min(host.right, window.innerWidth) + EDGE_TOLERANCE_PX;
    const bottom = Math.min(host.bottom, window.innerHeight) + EDGE_TOLERANCE_PX;

    const cards = this.querySelectorAll(CARD_SELECTOR);
    const rects = [...cards].map((card) => card.getBoundingClientRect());

    cards.forEach((card, index) => {
      const rect = rects[index];
      if (!rect) return;

      const inside =
        rect.top >= top && rect.bottom <= bottom && rect.left >= left && rect.right <= right;

      card.classList.toggle(IN_VIEW_CLASS, inside);
    });
  }
}

if (!customElements.get('landing-floating-grid')) {
  customElements.define('landing-floating-grid', LandingFloatingGrid);
}
